import { useState } from "react";
import { Modal } from "./Modal";
import type { DeletionBody, DeletionPreview } from "../api/portal";
import { dateTime } from "../format";

interface Props {
  preview: DeletionPreview;
  busy?: boolean;
  error?: string | null;
  onSubmit: (body: DeletionBody) => void;
  onCancel: () => void;
}

/** Deletes the whole report at FINTRAC (F2R DELETE). Three steps: what goes,
 *  why, and the customer's acknowledgement (spec 2026-10-09 §8.2). */
export function FintracDeletionDialog({ preview, busy, error, onSubmit, onCancel }: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(error ? 3 : 1);
  const [reason, setReason] = useState<DeletionBody["reason"] | null>(null);
  const [note, setNote] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [typed, setTyped] = useState("");
  const n = preview.transaction_count;
  const canDelete = !!reason && acknowledged
    && typed.trim().toLowerCase() === preview.confirm_text.toLowerCase() && !busy;

  const submit = () => {
    const trimmed = note.trim();
    onSubmit({ reason: reason!, ...(trimmed ? { note: trimmed } : {}),
               ack_version: preview.ack_version, fingerprint: preview.fingerprint,
               confirm_reference: typed.trim() });
  };

  return (
    <Modal title="Delete report from FINTRAC" size="medium" onClose={onCancel}>
      <h3>Delete {preview.report_type} {preview.confirm_text} from FINTRAC</h3>
      {step === 1 && (
        <>
          <ul className="consequences">
            <li>This deletes the entire report at FINTRAC. All {n} {n === 1 ? "transaction" : "transactions"} below will no longer be reported.</li>
            <li>Scadox will not re-file them.</li>
            {preview.deletable_until && <li>Deletion is possible until {dateTime(preview.deletable_until)}.</li>}
          </ul>
          {preview.split_siblings.length > 0 && (
            <p className="empty">The other parts of this split report are not deleted: {preview.split_siblings.map((s) => s.slice(0, 8)).join(", ")}.</p>
          )}
          <div className="deletion-txns">
          <table className="data-table">
            <thead><tr><th>Transaction</th><th>Date</th><th className="num">Amount</th><th className="num">CAD</th><th>Sender → Receiver</th></tr></thead>
            <tbody>
              {preview.transactions.map((t) => (
                <tr key={t.reference}>
                  <td>{t.reference}</td>
                  <td>{dateTime(t.transaction_date_time)}</td>
                  <td className="num">{t.amount} {t.currency}</td>
                  <td className="num">{t.amount_cad}</td>
                  <td>{t.sender ?? "—"} → {t.receiver ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="modal-actions">
            <button onClick={onCancel}>Cancel</button>
            <button onClick={() => setStep(2)}>Continue</button>
          </div>
        </>
      )}
      {step === 2 && (
        <>
          <fieldset>
            <legend>Reason (sent to FINTRAC)</legend>
            {preview.reasons.map((r) => (
              <label key={r.code} className="choice-row">
                <input type="radio" name="reason" checked={reason === r.code}
                       onChange={() => setReason(r.code)} />
                <span><strong>{r.label}</strong> — {r.help}</span>
              </label>
            ))}
          </fieldset>
          <label>
            Note (optional, visible to Scadox)
            <textarea maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="modal-actions">
            <button onClick={() => setStep(1)}>Back</button>
            <button onClick={() => setStep(3)} disabled={!reason}>Continue</button>
          </div>
        </>
      )}
      {step === 3 && (
        <>
          <label className="choice-row">
            <input type="checkbox" checked={acknowledged}
                   onChange={(e) => setAcknowledged(e.target.checked)} />
            <span>{preview.ack_text}</span>
          </label>
          <label>
            Type {preview.confirm_text} to confirm
            <input value={typed} onChange={(e) => setTyped(e.target.value)} />
          </label>
          {error && <p className="empty">{error}</p>}
          <div className="modal-actions">
            <button onClick={() => setStep(2)} disabled={busy}>Back</button>
            <button className="btn-danger" onClick={submit} disabled={!canDelete}>
              {busy ? "Deleting…" : "Delete from FINTRAC"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
